import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, message, Modal } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import getApolloErrors from '../../../../server/utils/getApolloErrors';
import * as permissionKind from '../../../../shared/permissions';
import { type BannerDataFragment } from '../../../api/fragments/BannerData';
import { type UploadFileWithPreviewFormDataFragment } from '../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteBannerDocument,
    type DeleteBannerMutation,
    type DeleteBannerMutationVariables,
} from '../../../api/mutations/deleteBanner';
import {
    UpdateBannerDocument,
    type UpdateBannerMutation,
    type UpdateBannerMutationVariables,
} from '../../../api/mutations/updateBanner';
import { type BannerInput } from '../../../api/types';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import BannerForm from './BannerForm';
import useUploadBannerImage from './useUploadBannerImage';
import useBannerValidator from './useValidator';

export type BannerFormikValue = BannerInput & {
    moduleId?: string;
    // as of 14/02/2022, we support for single banner, in future we might enhance to multiple images

    bannerImage?: File | UploadFileWithPreviewFormDataFragment;
    mobileBannerImage?: File | UploadFileWithPreviewFormDataFragment;
};

type BannerDetailsPageProps = {
    banner: BannerDataFragment;
};

const BannerDetailsInnerPage = ({ banner }: BannerDetailsPageProps) => {
    const { t } = useTranslation('bannerDetails');
    const navigate = useNavigate();
    const apolloClient = useApolloClient();
    const handleBannerAsset = useUploadBannerImage();
    const initialValues: BannerFormikValue = useMemo(
        () => ({
            ...pick(['bannerText', 'isActive', 'bannerTextPosition', 'bannerImage', 'mobileBannerImage'], banner),
        }),
        [banner]
    );

    const validate = useBannerValidator(true);

    const onSubmit = useHandleError<BannerFormikValue>(
        async value => {
            // submitting message
            message.loading({
                content: t('bannerDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { bannerImage, mobileBannerImage, moduleId, ...settings } = value;

            await apolloClient.mutate<UpdateBannerMutation, UpdateBannerMutationVariables>({
                mutation: UpdateBannerDocument,
                variables: {
                    id: banner.id,
                    settings,
                },
            });

            await handleBannerAsset(banner.id, value, banner);

            // inform about success
            message.success({
                content: t('bannerDetails:messages.createSuccessful'),
                key: 'primary',
            });

            navigate('/admin/banners');
        },
        [apolloClient, banner, handleBannerAsset, navigate, t]
    );

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: banner.versioning,
        moduleId: banner.module.id,
    });

    const onDeleteBanner = useCallback(async () => {
        Modal.confirm({
            title: t('bannerDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('bannerDetails:deleteModal.content'),
            okText: t('bannerDetails:deleteModal.okText'),
            okType: 'danger',
            cancelText: t('bannerDetails:deleteModal.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('bannerDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    await apolloClient.mutate<DeleteBannerMutation, DeleteBannerMutationVariables>({
                        mutation: DeleteBannerDocument,
                        variables: {
                            id: banner.id,
                        },
                    });

                    // show success
                    message.success({
                        content: t('bankDetails:messages.deleteSuccessful'),
                        key: 'primary',
                    });

                    // then go back to list
                    navigate('/admin/banners');
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    }
                }
            },
        });
    }, [apolloClient, banner.id, navigate, t]);

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(banner.permissions, [permissionKind.updateBanner]),
            hasPermissions(banner.permissions, [permissionKind.deleteBanner]),
        ],
        [banner.permissions]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <ConsolePageWithHeader
                    extra={
                        hasDeletePermission && (
                            <Button onClick={onDeleteBanner} danger>
                                {t('bannerDetails:actions.delete')}
                            </Button>
                        )
                    }
                    footer={[
                        hasUpdatePermission && (
                            <Button key="bannerUpdate" form="bannerUpdate" htmlType="submit" type="primary">
                                {t('bannerDetails:actions.update')}
                            </Button>
                        ),
                    ]}
                    onBack={() => navigate('/admin/banners')}
                    title={t('bannerDetails:title')}
                >
                    <Form id="bannerUpdate" name="bannerUpdate" onSubmitCapture={handleSubmit}>
                        <FormAutoTouch />
                        <BannerForm banner={banner} disabled={!hasUpdatePermission} offset={offset} updated={updated} />
                    </Form>
                </ConsolePageWithHeader>
            )}
        </Formik>
    );
};

export default BannerDetailsInnerPage;
